import React from 'react';
import Icon from './Icon';
import { useT } from '../i18n/LanguageContext';

interface FallbackSelectorProps {
  fallbackEnabled: boolean;
  fallbackSmsContent: string;
  onChange: (enabled: boolean, content: string) => void;
}

const FallbackSelector: React.FC<FallbackSelectorProps> = ({
  fallbackEnabled,
  fallbackSmsContent,
  onChange,
}) => {
  const t = useT();
  return (
    <div
      className="card card-pad"
      style={{ background: 'var(--surface-2)', marginBottom: 20 }}
    >
      <h3 className="card-title" style={{ marginTop: 0, marginBottom: 15 }}>
        <Icon name="zap" size={16} /> {t('Strategija isporuke', 'Delivery Strategy')}
      </h3>

      <div
        style={{
          padding: 15,
          background: 'var(--ch-sms-soft)',
          borderRadius: 8,
          marginBottom: 15,
          fontSize: 14,
        }}
      >
        <strong>
          <Icon name="info" size={16} /> {t('Kako radi rezervni kanal:', 'How fallback works:')}
        </strong>
        <ol style={{ marginBottom: 0, marginTop: 8, paddingLeft: 20 }}>
          <li>
            <strong>{t('Primarno:', 'Primary:')}</strong> {t('Šalje se Viber poruka (rich sadržaj sa dugmadima/slikama)', 'Send Viber message (rich content with buttons/images)')}
          </li>
          <li>
            <strong>{t('Rezervno:', 'Fallback:')}</strong> {t('Ako Viber ne uspe', 'If Viber fails')} <Icon name="arrowRight" size={16} />{' '}
            {t('automatski se šalje SMS (običan tekst)', 'automatically send SMS (simple text)')}
          </li>
        </ol>
      </div>

      {/* SMS fallback toggle card */}
      <div
        className="card"
        style={{
          padding: 16,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          marginBottom: 15,
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-12">
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--ch-sms-soft)',
                color: 'var(--ch-sms)',
                display: 'grid',
                placeItems: 'center',
                flex: 'none',
              }}
            >
              <Icon name="sms" size={18} />
            </span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{t('Uključi SMS rezervu', 'Enable SMS Fallback')}</div>
              <div className="hint" style={{ marginTop: 2 }}>
                {t('Automatski šalje SMS ako isporuka Viber poruke ne uspe (preporučeno)', 'Automatically send SMS if Viber delivery fails (recommended)')}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={'switch' + (fallbackEnabled ? ' on' : '')}
            aria-pressed={fallbackEnabled}
            onClick={() => onChange(!fallbackEnabled, fallbackSmsContent)}
          />
        </div>
      </div>

      {fallbackEnabled && (
        <div
          className="card"
          style={{ padding: 15, background: 'var(--surface)', border: '1px solid var(--brand-200)' }}
        >
          <label className="label" style={{ display: 'block', color: 'var(--brand-700)' }}>
            <Icon name="phone" size={16} /> {t('Sadržaj SMS rezerve', 'SMS Fallback Content')} *
          </label>

          <div
            style={{
              fontSize: 13,
              color: 'var(--text-2)',
              marginBottom: 10,
              padding: 8,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}
          >
            <Icon name="alert" size={16} /> {t('SMS ne podržava rich sadržaj (bez dugmadi, slika, videa). Neka bude jednostavan i ispod 160 znakova za jedan SMS.', "SMS doesn't support rich content (no buttons, images, videos). Keep it simple and under 160 characters for single SMS.")}
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <textarea
              className="textarea"
              value={fallbackSmsContent}
              onChange={(e) => onChange(fallbackEnabled, e.target.value)}
              placeholder={t('Primer: Letnja rasprodaja! {{name}}, do 50% popusta. Posetite: shop.com/sale', 'Example: Letnja rasprodaja! {{name}}, do 50% popusta. Posetite: shop.com/sale')}
              rows={4}
              maxLength={300}
              required={fallbackEnabled}
              style={{ minHeight: 90 }}
            />
          </div>

          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ color: 'var(--text-2)' }}>
              {fallbackSmsContent.length} {t('znakova', 'characters')}
              {fallbackSmsContent.length <= 160 && ` (1 SMS)`}
              {fallbackSmsContent.length > 160 && fallbackSmsContent.length <= 306 && ` (2 SMS)`}
              {fallbackSmsContent.length > 306 && ` (${Math.ceil(fallbackSmsContent.length / 153)} SMS)`}
            </span>
            {fallbackSmsContent.length > 160 && (
              <span style={{ color: 'var(--danger)', fontWeight: 'bold' }}>
                <Icon name="alert" size={16} /> {t('Biće podeljeno na više SMS poruka', 'Will be split into multiple SMS')}
              </span>
            )}
          </div>

          <div
            style={{
              marginTop: 12,
              padding: 10,
              background: 'var(--ch-sms-soft)',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <strong>
              <Icon name="info" size={16} /> {t('Promenljive rade i u SMS-u:', 'Placeholders work in SMS too:')}
            </strong>
            <ul style={{ marginTop: 5, marginBottom: 0, paddingLeft: 20 }}>
              <li>
                <code>{'{{name}}'}</code> - {t('Ime primaoca', 'Recipient name')}
              </li>
              <li>
                <code>{'{{phone}}'}</code> - {t('Broj telefona', 'Phone number')}
              </li>
              <li>
                <code>{'{{custom.fieldName}}'}</code> - {t('Prilagođena polja', 'Custom fields')}
              </li>
            </ul>
          </div>
        </div>
      )}

      {!fallbackEnabled && (
        <div
          style={{
            padding: 12,
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 13,
            color: 'var(--text-2)',
          }}
        >
          <Icon name="alert" size={16} /> <strong>{t('Upozorenje:', 'Warning:')}</strong> {t("Ako isporuka Viber poruke ne uspe (npr. korisnik nema Viber), poruka neće biti isporučena. Uključite SMS rezervu za 99% stopu isporuke.", "If Viber delivery fails (e.g., user doesn't have Viber), the message won't be delivered. Enable SMS fallback for 99% delivery rate.")}
        </div>
      )}

      <div
        className="card"
        style={{
          marginTop: 15,
          padding: 12,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          fontSize: 13,
        }}
      >
        <strong>
          <Icon name="trendUp" size={16} /> {t('Procenjeni trošak:', 'Estimated Cost:')}
        </strong>
        <div style={{ marginTop: 8 }}>
          {!fallbackEnabled && <div>• {t('Samo Viber: ~€0.03 po poruci', 'Viber only: ~€0.03 per message')}</div>}
          {fallbackEnabled && (
            <>
              <div>• {t('Viber isporučen: ~€0.03', 'Viber delivered: ~€0.03')}</div>
              <div>
                • {t('Viber neuspeo', 'Viber failed')} <Icon name="arrowRight" size={16} /> {t('SMS poslat: ~€0.03 + €0.05 = €0.08', 'SMS sent: ~€0.03 + €0.05 = €0.08')}
              </div>
              <div style={{ marginTop: 5, color: 'var(--text-2)', fontSize: 12 }}>
                {t('(SMS rezerva se naplaćuje samo ako isporuka Viber poruke ne uspe)', '(SMS fallback only charged if Viber delivery fails)')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FallbackSelector;
